import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RatesData, GetRatesAndRosterResponse } from '../../pages/rosterAndRates/model';

// Interface for category override data (roster adjustments - hours only)
export interface CategoryOverride {
  override?: number; // Hours override only
  overrideReason?: string;
  overrideComment?: string;
}

// Interface for rate override data
export interface RateOverride {
  override?: number; // Rate override (UPH)
  overrideReason?: string;
  overrideComment?: string;
  processPath?: string;
  functionId?: string;
  functionName?: string;
  functionType?: string;
}

// Interface for total attendance override data
export interface TotalAttendanceOverride {
  override: number | null;
  overrideReason?: string | null;
  overrideComment?: string | null;
}

// Main state interface
export interface RosterAndRatesState {
  totalAttendanceOverride: TotalAttendanceOverride | null;
  categoryOverrides: Record<string, CategoryOverride>; // Category name as key
  rateOverrides: Record<string, RateOverride>; // Function ID as key
  ratesAndRoster: GetRatesAndRosterResponse | null; // Store the complete rates and roster data
  ratesDataLoading: boolean; // Loading state for rates data API call
  loading: boolean; // General loading state
  retryCount: number;
  error: string | null;
}

// Initial state
const initialState: RosterAndRatesState = {
  totalAttendanceOverride: null,
  categoryOverrides: {},
  rateOverrides: {},
  ratesAndRoster: null,
  ratesDataLoading: false,
  loading: false,
  retryCount: 0,
  error: null,
};

// Payload interfaces for actions
export interface UpdateCategoryOverridePayload {
  categoryName: string;
  field: 'override' | 'overrideReason' | 'overrideComment';
  value: number | string | undefined | null;
}

export interface UpdateRateOverridePayload {
  functionId: string;
  field: 'override' | 'overrideReason' | 'overrideComment';
  value: number | string | undefined | null;
}

export interface UpdateTotalAttendanceOverridePayload {
  field: 'override' | 'overrideReason' | 'overrideComment';
  value: number | string;
}

export interface SaveCategoryOverridePayload {
  categoryName: string;
  override: number;
  overrideReason: string;
  overrideComment: string;
}

export interface SaveTotalAttendanceOverridePayload {
  override: number;
  overrideReason: string;
  overrideComment: string;
}

// Create the slice
const rosterAndRatesSlice = createSlice({
  name: 'rosterAndRates',
  initialState,
  reducers: {
    // Update individual category override fields
    updateCategoryOverride: (state, action: PayloadAction<UpdateCategoryOverridePayload>) => {
      const { categoryName, field, value } = action.payload;
      
      if (!state.categoryOverrides[categoryName]) {
        state.categoryOverrides[categoryName] = {};
      }
      
      if (field === 'override') {
        state.categoryOverrides[categoryName].override = value as number;
      } else if (field === 'overrideReason') {
        state.categoryOverrides[categoryName].overrideReason = value as string;
      } else if (field === 'overrideComment') {
        state.categoryOverrides[categoryName].overrideComment = value as string;
      }
    },

    // Update rate override fields
    updateRateOverride: (state, action: PayloadAction<UpdateRateOverridePayload>) => {
      const { functionId, field, value } = action.payload;
      
      if (!state.rateOverrides[functionId]) {
        state.rateOverrides[functionId] = {};
      }
      
      if (field === 'override') {
        state.rateOverrides[functionId].override = value as number;
      } else if (field === 'overrideReason') {
        state.rateOverrides[functionId].overrideReason = value as string;
      } else if (field === 'overrideComment') {
        state.rateOverrides[functionId].overrideComment = value as string;
      }
    },

    // Update total attendance override fields
    updateTotalAttendanceOverride: (state, action: PayloadAction<UpdateTotalAttendanceOverridePayload>) => {
      const { field, value } = action.payload;
      
      if (!state.totalAttendanceOverride) {
        state.totalAttendanceOverride = {
          override: field === 'override' ? (value as number) : null,
          overrideReason: field === 'overrideReason' ? (value as string) : '',
          overrideComment: field === 'overrideComment' ? (value as string) : ''
        };
      } else {
        // Preserve existing values and only update the specified field
        if (field === 'override') {
          state.totalAttendanceOverride.override = value as number;
        } else if (field === 'overrideReason') {
          state.totalAttendanceOverride.overrideReason = value as string;
        } else if (field === 'overrideComment') {
          state.totalAttendanceOverride.overrideComment = value as string;
        }
      }
    },

    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    // Set error state
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    // Increment retry count
    incrementRatesAndRosterRetryCount: (state) => {
       state.retryCount = state.retryCount + 1;
    },

    // Initialize category overrides from fetched data
    initializeCategoryOverrides: (state, action: PayloadAction<Record<string, CategoryOverride>>) => {
      state.categoryOverrides = action.payload;
    },

    // Initialize total attendance override from fetched data
    initializeTotalAttendanceOverride: (state, action: PayloadAction<TotalAttendanceOverride>) => {
      state.totalAttendanceOverride = action.payload;
    },

    // Clear all overrides
    clearAllOverrides: (state) => {
      state.categoryOverrides = {};
      state.rateOverrides = {};
      state.totalAttendanceOverride = null;
    },

    // Clear specific category override
    clearCategoryOverride: (state, action: PayloadAction<string>) => {
      const categoryName = action.payload;
      delete state.categoryOverrides[categoryName];
    },

    // Set rates and roster data
    setRatesAndRoster: (state, action: PayloadAction<GetRatesAndRosterResponse>) => {
      state.ratesAndRoster = action.payload;
      // Initialize overrides from the roster data only if they don't already exist
      if (action.payload.rosterData) {
        // Initialize total attendance override if it exists in response AND no override is currently set
        if (!state.totalAttendanceOverride &&
            (action.payload.rosterData.totalAttendance?.override || 
             action.payload.rosterData.totalAttendance?.overrideReason ||
             action.payload.rosterData.totalAttendance?.overrideComment)) {
          state.totalAttendanceOverride = {
            override: action.payload.rosterData.totalAttendance.override || 0,
            overrideReason: action.payload.rosterData.totalAttendance.overrideReason,
            overrideComment: action.payload.rosterData.totalAttendance.overrideComment,
          };
        }
        
        // Initialize category overrides if they exist in response and don't already exist
        action.payload.rosterData.categories?.forEach((category) => {
          if (!state.categoryOverrides[category.name] &&
              (category.override || category.overrideReason || category.overrideComment)) {
            state.categoryOverrides[category.name] = {
              override: category.override,
              overrideReason: category.overrideReason,
              overrideComment: category.overrideComment,
            };
          }
        });
      }

      // Initialize rate overrides from function rates if backend returns override data.
      // The backend may include overrideRate/overrideReason/overrideComment on each
      // function rate entry when a plan has previously-saved rate overrides.
      if (action.payload.ratesData?.functionRates) {
        action.payload.ratesData.functionRates.forEach((funcRate) => {
          // Backend may send optional override fields not yet in the base FunctionRate type.
          // Access via bracket notation to safely extract them without `any` casts.
          const rateRecord = funcRate as unknown as Record<string, unknown>;
          const overrideRate = rateRecord.overrideRate as number | undefined;
          const overrideReason = rateRecord.overrideReason as string | undefined;
          const overrideComment = rateRecord.overrideComment as string | undefined;
          if (!state.rateOverrides[funcRate.functionId] &&
              (overrideRate || overrideReason || overrideComment)) {
            state.rateOverrides[funcRate.functionId] = {
              override: overrideRate,
              overrideReason: overrideReason,
              overrideComment: overrideComment,
              processPath: funcRate.processPath,
              functionId: funcRate.functionId,
              functionName: funcRate.functionName,
              functionType: funcRate.functionType,
            };
          }
        });
      }
    },

    // Set rates data loading state
    setRatesDataLoading: (state, action: PayloadAction<boolean>) => {
      state.ratesDataLoading = action.payload;
    },

    // Clear rates and roster data
    clearRatesAndRoster: (state) => {
      state.ratesAndRoster = null;
    },

  },
});

// Export actions
export const {
  updateCategoryOverride,
  updateRateOverride,
  updateTotalAttendanceOverride,
  setLoading,
  setError,
  initializeCategoryOverrides,
  initializeTotalAttendanceOverride,
  clearAllOverrides,
  clearCategoryOverride,
  setRatesAndRoster,
  setRatesDataLoading,
  clearRatesAndRoster,
  incrementRatesAndRosterRetryCount,
} = rosterAndRatesSlice.actions;

// Export reducer
export default rosterAndRatesSlice.reducer;
